import { lazy, Suspense, useState, useEffect } from "react";
import { deckRouteKind, findFirstLeaf, findNodeInTree } from "./utils/treeNav";
import MovedGate from "./components/docs/MovedGate";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProjectProvider, useProject } from "./contexts/ProjectContext";

import { AppShellProvider, useAppShell } from "./contexts/AppShellContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { PreviewProvider, usePreview } from "./contexts/PreviewContext";
import { ReadOnlyProvider } from "./contexts/ReadOnlyContext";
import { RouteContextProvider, useRouteContext } from "./contexts/RouteContext";
import { ToastProvider } from "./components/docs/ToastNotification";
import { useCms as useCmsHook } from "./hooks/useCms";
import { authFetch } from "./utils/authFetch";
import { detectSurface } from "./utils/surfaceDetection";
import ProtectedRoute from "./components/ProtectedRoute";
import AppGuard from "./components/AppGuard";
import LayoutShell from "./components/shell/LayoutShell";
const DocsShell = lazy(() => import("./components/shell/DocsShell"));
const EnableShell = lazy(() => import("./components/shell/EnableShell"));
import SettingsLayout from "./components/SettingsLayout";
import LoginPage from "./pages/LoginPage";
import AtlasCallbackPage from "./pages/AtlasCallbackPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";

// Lazy-loaded pages
const AppSelector = lazy(() => import("./pages/AppSelector"));
const DocsLayout = lazy(() => import("./components/docs/DocsLayout"));
const ContentPageView = lazy(() => import("./pages/ContentPageView"));
const DocPageEditor = lazy(() => import("./pages/DocPageEditor"));
const ShareSessionRoute = lazy(() => import("./components/ShareSessionRoute"));
import ShareGuard from "./components/ShareGuard";
const CardEditorPage = lazy(() => import("./pages/CardEditorPage"));
const BulkImportPage = lazy(() => import("./pages/BulkImportPage"));
const PrintRoute = lazy(() => import("./print/PrintRoute"));
const HistoryView = lazy(() => import("./pages/HistoryView"));

const LocalPreviewManagePage = lazy(() => import("./pages/LocalPreviewManagePage"));
const CompleteProfilePage = lazy(() => import("./pages/CompleteProfilePage"));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-screen text-sm text-slate-400">
      <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mr-2" />
      Loading...
    </div>
  );
}

/** Entitlement-based home redirect: 1 app → auto-redirect, multiple → App Selector */
function HomeRedirect() {
  const { entitledApps, isLoading } = useAppShell();
  const { isLocalPreview, projectSlug: previewProject } = usePreview();

  // In local preview mode, skip the app selector and go straight to the docs editor.
  // Redirect to the project root — CmsProjectSelector will resolve the first section.
  if (isLocalPreview && previewProject) {
    return <Navigate to="/studio" replace />;
  }

  if (isLoading) return <LazyFallback />;

  // On a dedicated surface (enable.joveo.ai / docs.joveo.ai), go straight to that
  // app and skip the selector even if the user has several apps — they switch
  // apps from inside the shell. Only redirect if the user is actually entitled.
  const surface = detectSurface();
  if (surface.isDedicatedSurface && surface.appSlug) {
    const surfaceApp = entitledApps.find((a) => a.slug === surface.appSlug);
    if (surfaceApp) {
      return <Navigate to={surfaceApp.defaultPath} replace />;
    }
  }

  // If exactly 1 entitled app, go straight there
  if (entitledApps.length === 1) {
    return <Navigate to={entitledApps[0].defaultPath} replace />;
  }

  // Multiple → App Selector
  if (entitledApps.length > 1) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <AppSelector />
      </Suspense>
    );
  }

  // No entitlements — show App Selector (it handles the empty state)
  return (
    <Suspense fallback={<LazyFallback />}>
      <AppSelector />
    </Suspense>
  );
}

/** Renders the right page based on the active section type */
function SectionRouter() {
  const { "*": path, sectionSlug } = useParams();
  const { activeSection, tree } = useCmsFromContext();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  // Presentations sections use the CardEditor for all paths.
  // Check both activeSection (may lag during switch) and the URL sectionSlug.
  // A deck in any other section gets it too, for its own paths only.
  const deckKind = deckRouteKind(tree, path);
  if (activeSection?.sectionType === "presentations" || sectionSlug === "presentations" || deckKind === "card") {
    return (
      <Suspense fallback={<LazyFallback />}>
        <CardEditorPage />
      </Suspense>
    );
  }

  if (deckKind === "deck") {
    const first = findFirstLeaf(findNodeInTree(tree, (path ?? "").split("/").filter(Boolean))?.children ?? [], "");
    const qs = searchParams.toString();
    return <Navigate to={`${pathname.replace(/\/$/, "")}/${first}${qs ? `?${qs}` : ""}`} replace />;
  }

  if (path?.endsWith("/edit")) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <DocPageEditor />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LazyFallback />}>
      <SectionContent path={path} />
    </Suspense>
  );
}

/** Renders the unified content page for all section types */
function SectionContent({ path: _path }: { path?: string }) {
  return <ContentPageView />;
}

/** Section index */
function SectionIndex() {
  const { activeSection } = useCmsFromContext();
  const [searchParams] = useSearchParams();

  if ((activeSection?.sectionType === "content" || activeSection?.sectionType === "release-notes") && activeSection?.homePage) {
    const qs = searchParams.toString();
    const target = qs ? `${activeSection.homePage}?${qs}` : activeSection.homePage;
    return <Navigate to={target} replace />;
  }

  return (
    <Suspense fallback={<LazyFallback />}>
      <SectionContent />
    </Suspense>
  );
}

function useCmsFromContext() {
  return useCmsHook();
}

/** Redirect /studio/projects/:projectSlug → first section slug */
function ProjectSectionRedirect() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { switchProject, projects, loading: projectsLoading } = useProject();
  const [firstSection, setFirstSection] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (projectSlug) switchProject(projectSlug);
  }, [projectSlug, switchProject]);

  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/projects/${projectSlug}/studio/sections`);
        if (cancelled || !res.ok) { if (!cancelled) setLoaded(true); return; }
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) setFirstSection(data[0].slug);
      } catch { /* ignore */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [projectSlug]);

  if (projectSlug && firstSection) {
    return <Navigate to={`/studio/projects/${projectSlug}/${firstSection}`} replace />;
  }
  if (!loaded || projectsLoading) return <LazyFallback />;

  // Check if project exists
  const projectExists = projects.some(p => p.slug === projectSlug);
  if (!projectExists) {
    return (
      <AppGuard slug="studio">
        <Suspense fallback={<LazyFallback />}>
          <LayoutShell>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-slate-300 text-4xl mb-3">🔍</div>
                <div className="text-[15px] font-medium text-slate-600 mb-1">Project not found</div>
                <div className="text-[13px] text-slate-400">The project "{projectSlug}" doesn't exist or you don't have access.</div>
              </div>
            </div>
          </LayoutShell>
        </Suspense>
      </AppGuard>
    );
  }

  // Empty project — render the standard layout. DocsLayout handles "no sections" state.
  return (
    <AppGuard slug="studio">
      <Suspense fallback={<LazyFallback />}>
        <LayoutShell>
          <Suspense fallback={<LazyFallback />}><DocsLayout /></Suspense>
        </LayoutShell>
      </Suspense>
    </AppGuard>
  );
}

/** Redirect /docs/projects/:projectSlug → first section slug */
function ProjectSectionRedirectDocs() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { switchProject, projects, loading: projectsLoading } = useProject();
  const { appPrefix } = useRouteContext();
  const [firstSection, setFirstSection] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (projectSlug) switchProject(projectSlug);
  }, [projectSlug, switchProject]);

  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/docs/${projectSlug}/sections`);
        if (cancelled || !res.ok) { if (!cancelled) setLoaded(true); return; }
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) setFirstSection(data[0].slug);
      } catch { /* ignore */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [projectSlug]);

  if (projectSlug && firstSection) {
    return <Navigate to={`${appPrefix}/projects/${projectSlug}/${firstSection}`} replace />;
  }
  if (!loaded || projectsLoading) return <LazyFallback />;

  const projectExists = projects.some(p => p.slug === projectSlug);
  if (!projectExists) {
    return (
      <ReadOnlyProvider>
        <AppGuard slug="docs">
          <Suspense fallback={<LazyFallback />}>
            <DocsShell>
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-slate-300 text-4xl mb-3">🔍</div>
                  <div className="text-[15px] font-medium text-slate-600 mb-1">Project not found</div>
                  <div className="text-[13px] text-slate-400">The project "{projectSlug}" doesn't exist or you don't have access.</div>
                </div>
              </div>
            </DocsShell>
          </Suspense>
        </AppGuard>
      </ReadOnlyProvider>
    );
  }

  return (
    <ReadOnlyProvider>
      <AppGuard slug="docs">
        <Suspense fallback={<LazyFallback />}><DocsShell /></Suspense>
      </AppGuard>
    </ReadOnlyProvider>
  );
}

/** /studio — pick project, redirect to project landing */
function CmsProjectSelector() {
  const { projects, currentProject, loading: projectsLoading } = useProject();
  const { isLocalPreview, projectSlug: previewProject } = usePreview();
  const { user } = useAuth();

  const targetSlug = isLocalPreview && previewProject
    ? previewProject
    : (currentProject?.slug || projects.find((p) => p.slug === "platform")?.slug || projects[0]?.slug);

  // Have a project → go to its landing page (ProjectSectionRedirect handles sections)
  if (targetSlug) {
    return <Navigate to={`/studio/projects/${targetSlug}`} replace />;
  }

  // Still loading
  if (projectsLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  // No projects at all
  const isAdmin = user?.isSuperAdmin || user?.userType === "internal";
  if (isAdmin) {
    return <Navigate to="/studio/settings/projects" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center max-w-sm">
        <h2 className="text-base font-semibold text-slate-700 mb-1">No projects yet</h2>
        <p className="text-sm text-slate-500">There are no documentation projects to show.</p>
      </div>
    </div>
  );
}

/** /docs — pick project, redirect to project landing */
function DocsProjectSelector() {
  const { projects, loading: projectsLoading } = useProject();

  // Only consider published projects for the docs app
  const publishedProjects = projects.filter((p) => p.isPublished !== false);

  const targetSlug = publishedProjects.length > 0
    ? (publishedProjects.find((p) => p.slug === "platform")?.slug || publishedProjects[0].slug)
    : null;

  if (targetSlug) {
    return <Navigate to={`/docs/projects/${targetSlug}`} replace />;
  }

  if (projectsLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center max-w-sm">
        <h2 className="text-base font-semibold text-slate-700 mb-1">No projects available</h2>
        <p className="text-sm text-slate-500">You don't have access to any documentation projects.</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
    <PreviewProvider>
    <AuthProvider>
            <BrowserRouter>
              <AppShellProvider>
                <ProjectProvider>
                <BrandingProvider>
                  <RouteContextProvider>
                  <Suspense fallback={<LazyFallback />}>
                    <Routes>
                        {/* Public */}
                        <Route path="/login" element={<LoginPage />} />
                        {/* Where an Atlas Identity sign-in lands. */}
                        <Route path="/auth/callback" element={<AtlasCallbackPage />} />
                        <Route path="/invite/:token" element={<InviteAcceptPage />} />
                        {/* Share-link entry point. Public — exchanges the share
                            token for a short-lived JWT, stores it in sessionStorage,
                            and redirects into the share-prefixed docs surface so
                            every page URL the visitor sees stays copyable as a
                            working share link. */}
                        <Route path="/share/:token" element={<Suspense fallback={<LazyFallback />}><ShareSessionRoute /></Suspense>} />

                        {/* Share-prefixed docs surface. Mounts the same
                            components as `/docs/*` but under `/share/:token/`
                            (no `/docs` segment — shares are always docs)
                            so internal navigation preserves the share scope.
                            ShareGuard re-exchanges the JWT if the visitor
                            opened a deep-link directly. */}
                        <Route path="/share/:token/projects/:projectSlug" element={
                          <ShareGuard>
                            <ReadOnlyProvider><ProjectSectionRedirectDocs /></ReadOnlyProvider>
                          </ShareGuard>
                        } />

                        <Route path="/share/:token/projects/:projectSlug/:sectionSlug" element={
                          <ShareGuard>
                            <ReadOnlyProvider>
                              <AppGuard slug="docs">
                                <Suspense fallback={<LazyFallback />}><DocsShell /></Suspense>
                              </AppGuard>
                            </ReadOnlyProvider>
                          </ShareGuard>
                        }>
                          <Route element={<Suspense fallback={<LazyFallback />}><DocsLayout /></Suspense>}>
                            <Route index element={<SectionIndex />} />
                            <Route path="*" element={<SectionRouter />} />
                          </Route>
                        </Route>
                        <Route path="/complete-profile" element={
                          <ProtectedRoute><Suspense fallback={<LazyFallback />}><CompleteProfilePage /></Suspense></ProtectedRoute>
                        } />

                        {/* Landing — entitlement-based redirect */}
                        <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />

                        {/* Docs — read-only viewer (decoupled from CMS) */}
                        <Route path="/docs" element={
                          <ProtectedRoute>
                            <ReadOnlyProvider><AppGuard slug="docs"><Suspense fallback={<LazyFallback />}><DocsShell /></Suspense></AppGuard></ReadOnlyProvider>
                          </ProtectedRoute>
                        }>
                          <Route index element={<DocsProjectSelector />} />
                        </Route>

                        <Route path="/docs/projects" element={<Navigate to="/docs" replace />} />

                        {/* Enable — partner onboarding/implementation/support */}
                        <Route path="/enable/*" element={
                          <ProtectedRoute>
                            <AppGuard slug="enable"><Suspense fallback={<LazyFallback />}><EnableShell /></Suspense></AppGuard>
                          </ProtectedRoute>
                        } />

                        <Route path="/docs/projects/:projectSlug" element={
                          <ProtectedRoute>
                            <ReadOnlyProvider><ProjectSectionRedirectDocs /></ReadOnlyProvider>
                          </ProtectedRoute>
                        } />

                        <Route path="/docs/projects/:projectSlug/:sectionSlug" element={
                          <ProtectedRoute>
                            <ReadOnlyProvider>
                              <AppGuard slug="docs">
                                <MovedGate><Suspense fallback={<LazyFallback />}><DocsShell /></Suspense></MovedGate>
                              </AppGuard>
                            </ReadOnlyProvider>
                          </ProtectedRoute>
                        }>
                          <Route element={<Suspense fallback={<LazyFallback />}><DocsLayout /></Suspense>}>
                            <Route index element={<SectionIndex />} />
                            <Route path="*" element={<SectionRouter />} />
                          </Route>
                        </Route>

                        {/* Export preview routes — chrome-stripped surface
                            for PDF export (studio-only; docs has no export). */}
                        <Route path="/studio/export/projects/:projectSlug/:sectionSlug/*" element={
                          <ProtectedRoute>
                            <Suspense fallback={<LazyFallback />}><PrintRoute /></Suspense>
                          </ProtectedRoute>
                        } />

                        {/* File history — read-only preview of one node at
                            a past commit. Reuses CmsProvider for branch
                            scoping but renders standalone (no shell). */}
                        <Route path="/studio/history/projects/:projectSlug/:sectionSlug/*" element={
                          <ProtectedRoute>
                            <Suspense fallback={<LazyFallback />}><HistoryView /></Suspense>
                          </ProtectedRoute>
                        } />

                        {/* Settings & Admin */}
                        <Route path="/studio/settings/*" element={<ProtectedRoute><SettingsLayout /></ProtectedRoute>} />

                        {/* CMS — internal docs editing (Studio) */}
                        <Route element={<ProtectedRoute />}>
                          <Route path="/studio" element={<CmsProjectSelector />} />
                          <Route path="/studio/projects" element={<Navigate to="/studio" replace />} />
                          <Route path="/studio/projects/:projectSlug" element={
                            <ProtectedRoute><ProjectSectionRedirect /></ProtectedRoute>
                          } />

                          <Route path="/studio/projects/:projectSlug/:sectionSlug" element={
                            <AppGuard slug="studio">
                              <Suspense fallback={<LazyFallback />}><LayoutShell /></Suspense>
                            </AppGuard>
                          }>
                            <Route element={<Suspense fallback={<LazyFallback />}><DocsLayout /></Suspense>}>
                              <Route index element={<SectionIndex />} />
                              <Route path="import" element={<BulkImportPage />} />
                              <Route path="*" element={<SectionRouter />} />
                            </Route>
                          </Route>
                        </Route>

                        <Route path="/studio/settings/local/projects/:projectSlug" element={
                          <ProtectedRoute>
                            <Suspense fallback={<LazyFallback />}><LocalPreviewManagePage /></Suspense>
                          </ProtectedRoute>
                        } />


                    </Routes>
                  </Suspense>
                  </RouteContextProvider>
                </BrandingProvider>
                </ProjectProvider>
              </AppShellProvider>
            </BrowserRouter>
    </AuthProvider>
    </PreviewProvider>
    </ToastProvider>
  );
}
